import React, { useEffect, useState } from 'react'
import HeadingNew from 'presentation/shared/components/HeadingNew'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import FormRegistrationInfo from 'presentation/doctor/components/Forms/SurgicalOrder/FormRegistrationInfo'
import { FormikProvider, useFormik } from 'formik'
import { useStores } from 'presentation/hooks/use-stores'
import { toJS } from 'mobx'
import validationSchema from './validationSchema'
import { useHistory, useLocation } from 'react-router'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import { useServices } from 'presentation/hooks/use-services'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react-lite'
import { OrderStepsPath } from 'presentation/contexts/surgical-order-v2/types'
import { RegistrationInfoFormValues } from 'presentation/contexts/surgical-order-v2/types'
import { isPatientRN } from 'presentation/utils/validators/is-patient-rn'
import moment from 'moment-timezone'
import { MaritalStatus } from 'presentation/utils/default-marital-status'

type RegistrationInfoFormProps = {
  initialValues: RegistrationInfoFormValues
} & WithLoadingProps

// Componente interno: só monta quando os dados já estão no store
// Garante que o useFormik capture o snapshot correto no initialValues
const RegistrationInfoForm = WithLoading(
  observer(({ initialValues, setIsLoading }: RegistrationInfoFormProps) => {
    const history = useHistory()
    const store = useStores().surgicalOrderV2
    const surgicalOrderService = useServices().surgicalOrder
    const patientService = useServices().patient

    const formik = useFormik({
      initialValues,
      validationSchema,
      onSubmit: async (values: RegistrationInfoFormValues) => {
        const reponseCreateNewPatient = await createNewPatient(values)
        const responseUpdateOrder = await updateSurgicalOrder(values)
        const responseCreateOrder = await createSurgicalOrder(values)
        const reponseUpdatePatient = await updatePatient(values)

        if (
          reponseUpdatePatient &&
          reponseCreateNewPatient &&
          responseUpdateOrder &&
          responseCreateOrder
        ) {
          store.markStepCompleted(store.currentStepIndex)
          history.replace(OrderStepsPath.PROCEDURE)
        }
      }
    })

    // TODO: Update Patient...
    const updatePatient = async (values: RegistrationInfoFormValues) => {
      if (!store.patient.isNewPatient) return true
      console.log('updatePatient values: ', values)
      console.log('Atualizar dados do paciente existente')
    }

    const createNewPatient = async (values: RegistrationInfoFormValues) => {
      if (!store.patient.isNewPatient) return true

      const formatedBirthdayDate = moment(
        values.birthDate,
        'YYYY-MM-DD'
      ).toISOString()
      const formatedPhone = values.phone?.replace(/\D/g, '')
      const formatedTutorPhone = values.tutor?.phone?.replace(/\D/g, '')
      const formatedCPF = values.cpf?.replace(/\D+/g, '')
      const formatedTutorCPF = values.tutor?.cpf?.replace(/\D+/g, '')

      const patientPayload = {
        hospital_id: values.hospitalId,
        sex: values.gender,
        name: values.name,
        marital_status: values.maritalStatus,
        birthday: formatedBirthdayDate,
        cpf: formatedCPF,
        phone: formatedPhone,
        email: values.email
      }

      const patientRNPayload = {
        hospital_id: values.hospitalId,
        sex: values.gender,
        marital_status: MaritalStatus.SINGLE,
        birthday: formatedBirthdayDate,
        name: values.name,
        cpf: formatedCPF || null,
        phone: formatedTutorPhone,
        email: values.tutor?.email,
        tutor_name: values.tutor?.name,
        tutor_cpf: formatedTutorCPF,
        tutor_phone: formatedTutorPhone,
        tutor_email: values.tutor?.email
      }

      const payload = isPatientRN(new Date(values.birthDate))
        ? patientRNPayload
        : patientPayload

      setIsLoading(true)
      try {
        const patient = await patientService.addPatientNew(payload)
        toast.success('Paciente cadastrado com sucesso')
        if (isPatientRN(new Date(values.birthDate))) {
          await store.loadPatientById(patient.data?.patient_id, patientService)
        } else {
          await store.loadPatientByCpf(formatedCPF, patientService)
        }
        return true
      } catch (error) {
        toast.error('Ocorreu um erro ao cadastrar novo paciente')
        return false
      } finally {
        setIsLoading(false)
      }
    }

    const updateSurgicalOrder = async (values: RegistrationInfoFormValues) => {
      if (!store.surgicalOrder?.surgicalOrderId) return true

      const payload = {
        surgical_order_id: store.surgicalOrder?.surgicalOrderId,
        hospital_id: values.hospitalId,
        hospital: {
          hospital_id: values.hospitalId,
          name: values.hospitalName ?? ''
        },
        surgical_center: {
          surgical_center_id: values.surgeryCenterId,
          surgical_center: values.surgeryCenterName
        },
        patient: {
          patient_id: store.patient?.patientId,
          patient_name: store.patient?.name
        },
        health_insurance: {
          // health_insurance_id: values.healthInsuranceId, // TODO: ??
          health_insurance_code: values.healthInsuranceCode,
          health_insurance_name: values.healthInsuranceName,
          health_insurance_card: values.healthCard,
          health_plan_code: values.healthPlanCode,
          health_plan_name: values.healthPlanName,
          health_sub_plan_code: values.healthSubplanCode,
          health_sub_plan_name: values.healthSubplanName,
          valid_thru: values.validThru
        },
        cooperative_name: values.cooperative
      }
      setIsLoading(true)
      try {
        await store.updateSurgicalOrder(surgicalOrderService, payload)
        return true
      } catch (error) {
        toast.error('Ocorreu um erro ao atualizar o pedido')
        return false
      } finally {
        setIsLoading(false)
      }
    }

    const createSurgicalOrder = async (values: RegistrationInfoFormValues) => {
      if (store.surgicalOrder?.surgicalOrderId) return true

      const payload = {
        hospital_id: values.hospitalId,
        surgical_center: {
          surgical_center_id: values.surgeryCenterId,
          surgical_center: values.surgeryCenterName
        },
        patient: {
          patient_id: store.patient?.patientId,
          patient_name: store.patient?.name
        },
        health_insurance: {
          health_insurance_code: values.healthInsuranceCode,
          health_insurance_name: values.healthInsuranceName,
          health_insurance_card: values.healthCard,
          health_plan_code: values.healthPlanCode,
          health_plan_name: values.healthPlanName,
          health_sub_plan_code: values.healthSubplanCode,
          health_sub_plan_name: values.healthSubplanName,
          // company_name: values.companyName, // TODO: ?
          valid_thru: values.validThru
        },
        cooperative_name: values.cooperative
      }
      setIsLoading(true)
      try {
        await store.createSurgicalOrder(surgicalOrderService, payload)
        return true
      } catch (error) {
        toast.error('Ocorreu um erro ao criar o pedido')
        return false
      } finally {
        setIsLoading(false)
      }
    }

    // Sincroniza validação do Formik com o Context para o botão do Layout
    // useEffect(() => {
    //   store.setIsStepValid(formik.isValid && !formik.isSubmitting)
    // }, [formik.isValid, formik.isSubmitting])

    // Escuta o clique do botão que está no Layout
    useEffect(() => {
      if (store.submitFormTriggered) {
        formik.submitForm()
        store.resetSubmitTrigger()
      }
    }, [store.submitFormTriggered])

    return (
      <>
        <HeadingNew
          as="h1"
          color="textSecondary"
          size="xlarge"
          style={{ lineHeight: 1 }}
        >
          Informações cadastrais
        </HeadingNew>
        <SupportTextNew
          color="textPrimary"
          fontFamily="secondary"
          size="medium"
          style={{ marginTop: '8px', marginBottom: '24px' }}
        >
          Preencha local de realização da cirurgia, dados do paciente e dados da
          cooperativa
        </SupportTextNew>

        <FormikProvider value={formik}>
          <FormRegistrationInfo />
        </FormikProvider>
      </>
    )
  })
)

// Componente outer: gerencia o carregamento do pedido existente
// Só renderiza o form quando o store já está populado
const RegistrationInfo = WithLoading(
  observer(({ setIsLoading }: WithLoadingProps) => {
    const { state } = useLocation<{ surgicalOrderId?: number }>()
    const history = useHistory()
    const [isReady, setIsReady] = useState(false)

    const store = useStores().surgicalOrderV2
    const surgicalOrderService = useServices().surgicalOrder
    const patientService = useServices().patient
    const hospitalService = useServices().hospital

    useEffect(() => {
      const init = async () => {
        setIsLoading(true)
        try {
          const tasks = [
            store.loadHospitals(hospitalService),
            store.loadHealthPlans(patientService)
          ]

          if (
            state?.surgicalOrderId &&
            !store.surgicalOrder?.orderWasLoadedInContext
          ) {
            await store.loadSurgicalOrder(
              state.surgicalOrderId,
              surgicalOrderService
            )

            const dependentTasks = []
            if (store.location.hospital?.hospitalId) {
              dependentTasks.push(
                store.loadSurgeryCenters(
                  store.location.hospital.hospitalId,
                  hospitalService
                )
              )
            }
            if (store.patient.patientId) {
              dependentTasks.push(
                store.loadPatientById(store.patient.patientId, patientService)
              )
            }
            await Promise.all(dependentTasks)
          }

          await Promise.all(tasks)

          if (state?.surgicalOrderId) {
            const firstIncomplete = store.firstIncompleteStepPath
            if (firstIncomplete !== OrderStepsPath.REGISTRATION) {
              history.replace(firstIncomplete)
              return
            }
          }
        } catch (error) {
          console.error(error)
          const errorMessage = state?.surgicalOrderId
            ? 'Ocorreu um erro ao carregar o Pedido cirúrgico'
            : 'Ocorreu um erro ao carregar os dados iniciais'
          toast.error(errorMessage)
          if (state?.surgicalOrderId) history.push('/home')
        } finally {
          setIsLoading(false)
          setIsReady(true)
        }
      }

      init()
    }, [])

    // Aguarda todo o init terminar antes de montar o form
    // para garantir que o initialValues do Formik capture o snapshot correto
    if (!isReady) return null
    const initialValues = toJS(store.stepRegistrationInfoFormValues)

    return (
      <RegistrationInfoForm key={store.formKey} initialValues={initialValues} />
    )
  })
)

export default RegistrationInfo
